import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Input } from '@/components/ui/input'
import type { InputProps } from '@/components/ui/input'

import type { FactorMethod } from './securityClient'

export function SecretField({
  label,
  error,
  ...props
}: InputProps & {
  label: string
  error?: string
}) {
  const id = useId()
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)
  return (
    <div
      className="sentry-block flex min-w-0 flex-col gap-2"
      data-sentry-block="true"
    >
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <div className="flex min-w-0 items-center gap-2">
        <div className="min-w-0 flex-1">
          <Input
            {...props}
            id={id}
            type={visible ? 'text' : 'password'}
            aria-invalid={!!error}
            aria-describedby={error ? `${id}-error` : undefined}
          />
        </div>
        <button
          type="button"
          onClick={() => setVisible(!visible)}
          aria-pressed={visible}
          className="rounded px-2 py-2 text-sm underline focus-visible:ring-2"
        >
          {t(visible ? 'security.hide' : 'security.show')}
          <span className="sr-only"> {label}</span>
        </button>
      </div>
      {error && (
        <p id={`${id}-error`} role="alert" className="text-sm text-dynamicRed">
          {error}
        </p>
      )}
    </div>
  )
}

export function FactorFields({
  method,
  code,
  onMethod,
  onCode,
  disabled = false,
}: {
  method: FactorMethod
  code: string
  onMethod: (value: FactorMethod) => void
  onCode: (value: string) => void
  disabled?: boolean
}) {
  const { t } = useTranslation()
  const id = useId()
  return (
    <fieldset disabled={disabled} className="flex min-w-0 flex-col gap-3">
      <legend className="mb-2 text-sm font-medium">
        {t('security.factorProof')}
      </legend>
      <label htmlFor={`${id}-method`} className="sr-only">
        {t('security.verificationMethod')}
      </label>
      <select
        id={`${id}-method`}
        value={method}
        onChange={(e) => onMethod(e.target.value as FactorMethod)}
        className="h-11 rounded-lg border border-neutralColor5 bg-baseColorHighlight px-3"
      >
        <option value="totp">{t('security.authenticatorCode')}</option>
        <option value="recovery_code">{t('security.recoveryCode')}</option>
      </select>
      <SecretField
        label={t(
          method === 'totp'
            ? 'security.authenticatorCode'
            : 'security.recoveryCode',
        )}
        value={code}
        onChange={(e) => onCode(e.target.value)}
        required
        autoComplete="one-time-code"
        inputMode={method === 'totp' ? 'numeric' : 'text'}
        pattern={method === 'totp' ? '[0-9]{6}' : undefined}
        maxLength={method === 'totp' ? 6 : 128}
      />
      <p className="text-sm text-neutralColor60">
        {t(method === 'totp' ? 'security.codeHelp' : 'security.recoveryHelp')}
      </p>
    </fieldset>
  )
}
